import {
	ContractSuretyType,
	contractSuretyTypeToLabelMap,
	SuretyBondStatus,
	SuretyType,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { bondFormFileUrlQueryKey, useFetchBondTerms } from "@portal/api/bond";
import { type SuretyBond, useFetchBond } from "@portal/api/bond/agent";
import { useBondComments } from "@portal/api/comments";
import { useBondFollowers } from "@portal/api/followers";
import {
	bondTermOrdinal,
	CurrentBondTermCard,
	commissionPercentageForBond,
	deriveTermCommission,
	findCurrentBondTerm,
} from "@portal/components/features/bond-terms";
import { messageDraftKey } from "@portal/components/features/message-draft";
import { CommentsSection } from "@portal/components/layout/CommentsSection";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { cn } from "@portal/components/lib/utils";
import { Button, Card, Icon } from "@portal/components/primitives";
import { BondAttachmentsTable } from "@portal/components/shared/attachments/BondAttachmentsTable";
import { MoreActionsMenu } from "@portal/components/ui/MoreActionsMenu";
import { type Tab, TabBar } from "@portal/components/ui/TabBar";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useRecentlyViewedEntities } from "@portal/hooks/useRecentlyViewed";
import {
	type ReportScrolledFromTop,
	useScrolledFromTopGestures,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import { type FC, useCallback, useEffect, useRef, type WheelEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetFileUrl } from "../../../api";
import type { NonEmptyArray } from "../../../shared/types";
import { type BondTabId, useBondTabNavigation } from "../../shared/bond/useBondTabNavigation";
import { AgentWorkflowLayout } from "../layout/AgentWorkflowLayout";
import { AgentBondDetailsCard } from "./AgentBondDetailsCard";
import { AgentBondFormTab } from "./AgentBondFormTab";
import { AgentBondPeriodsTab } from "./AgentBondPeriodsTab";
import { AgentBondStickyPanel } from "./AgentBondStickyPanel";
import { BondCard } from "./shared/BondCard";
import { CloseBondModal } from "./shared/CloseBondModal";

const ENTITY_VIEW_COLUMN_CLASS = "w-full max-w-[min(950px,calc(100%-96px))] mx-auto";

const AGENT_BOND_TABS: NonEmptyArray<Tab<object> & { id: BondTabId }> = [
	{ id: "overview", label: "Overview", Component: () => null },
	{ id: "bond-form", label: "Bond Form", Component: () => null },
	{
		id: "periods-and-adjustments",
		label: "Periods & Adjustments",
		Component: () => null,
	},
];

export const BondViewCore: FC<{ bond: SuretyBond }> = ({ bond }) => {
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();
	const { terms } = useFetchBondTerms(bond.accountId, bond.id, isCommercialSuretyEnabled);
	const currentTerm = findCurrentBondTerm(terms);
	const { comments, commentsLoading, createComment, createCommentLoading } = useBondComments(
		bond.accountId,
		bond.id,
	);
	const followersControl = useBondFollowers(bond.id);
	const { getFileUrl: getBondFormFileUrl, fileUrlLoading } = useGetFileUrl();
	const navigate = useNavigate();
	const { addRecentlyViewedEntity } = useRecentlyViewedEntities();
	const { activeTabId, setActiveTabId, headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } =
		useBondTabNavigation({ tabsEnabled: isCommercialSuretyEnabled });

	const scrollRef = useRef<HTMLDivElement | null>(null);

	const reportScrolledFromTop = useCallback<ReportScrolledFromTop>(
		(scrolledFromTop) => setHeaderCollapsed(scrolledFromTop),
		[setHeaderCollapsed],
	);

	const {
		onScroll: handlePageScroll,
		attachNativeWheel: attachPageWheel,
		decideWheel: decideChromeWheel,
	} = useScrolledFromTopGestures(reportScrolledFromTop, undefined, getHeaderCollapsed);

	const nativeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node;
			nativeWheelDisposerRef.current?.();
			nativeWheelDisposerRef.current = node ? attachPageWheel(node) : undefined;
		},
		[attachPageWheel],
	);

	const handleChromeWheel = (e: WheelEvent<HTMLDivElement>) => {
		const target = scrollRef.current;
		if (!target || (e.target instanceof Node && target.contains(e.target))) return;
		const decision = decideChromeWheel(target, e);
		if (decision.collapse) {
			reportScrolledFromTop(true);
			return;
		}
		if (decision.block) return;
		if (decision.expand) reportScrolledFromTop(false);
		target.scrollBy({ top: e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY });
	};

	const handleTabChange = useCallback(
		(tabId: string) => {
			setActiveTabId(tabId);
			scrollRef.current?.scrollTo?.({ top: 0, behavior: "instant" });
		},
		[setActiveTabId],
	);

	useEffect(() => {
		addRecentlyViewedEntity({ id: bond.id, entityType: "bond" });
	}, [bond.id, addRecentlyViewedEntity]);

	const closeBond = {
		id: bond.id,
		accountId: bond.accountId,
		suretyType: bond.suretyType,
		contractSuretyType: bond.contractSuretyType,
		bondType: bond.contractSuretyType,
		amount: bond.amount,
		number: bond.number,
	};

	const isOpenBid =
		bond.contractSuretyType === ContractSuretyType.bid && bond.status === SuretyBondStatus.open;

	const downloadBond = () =>
		getBondFormFileUrl({
			baseUrl: `/v2/surety/bonds/${bond.id}/bond-form`,
			queryKey: bondFormFileUrlQueryKey(bond.id, true),
			asDownload: true,
		});

	const actionButtons = (
		<div className="flex w-full flex-col gap-2">
			{isOpenBid ? (
				<Button
					color="primary"
					variant="solid"
					className="w-full"
					onClick={() =>
						bond.finalQuoteId
							? navigate(`/accounts/${bond.accountId}/requests/${bond.finalQuoteId}`)
							: navigate(`/requests/create?accountId=${bond.accountId}&bidBondId=${bond.id}`)
					}
					trackingId={bond.finalQuoteId ? "bond-agent-view-final" : "bond-agent-request-final"}
				>
					{bond.finalQuoteId ? "View Final Request" : "Request Final Bond"}
				</Button>
			) : (
				<Button
					color="primary"
					className="w-full"
					onClick={downloadBond}
					loading={fileUrlLoading}
					trackingId="bond-agent-download"
				>
					Download Bond
				</Button>
			)}
			{!isOpenBid && bond.status === "open" ? (
				<CloseBondModal
					bond={closeBond}
					button={
						<Button
							variant="outline"
							color="secondary"
							className="w-full"
							trackingId="bond-agent-close"
						>
							Close Bond
						</Button>
					}
				/>
			) : null}
			{isOpenBid ? (
				<MoreActionsMenu
					items={[
						{
							el: (
								<CloseBondModal
									bond={closeBond}
									button={
										<div className="flex items-center rounded-md hover:bg-gray-200/55 space-x-[8px] px-[8px] py-[5.5px] text-[13px] font-medium text-primary cursor-pointer">
											<span className="w-[16px] h-full">
												<Icon type="shield-slash" className="stroke-[1]" size="mini" />
											</span>
											<span>Close Bond</span>
										</div>
									}
								/>
							),
						},
						{
							label: "Download Bond",
							icon: <Icon type="arrow-down-tray" size="mini" />,
							onClick: downloadBond,
							disabled: fileUrlLoading,
							trackingId: "bond-agent-download",
						},
					]}
					panelClassName="mt-[10px]"
				/>
			) : null}
		</div>
	);

	return (
		<AgentWorkflowLayout
			title={`${contractSuretyTypeToLabelMap[bond.contractSuretyType]} Bond ${bond.number}`}
			contentClassName="bg-surface-light"
			hideActionButtons
		>
			<div
				className="w-full h-full flex flex-col min-h-0"
				onWheel={handleChromeWheel}
				data-testid="agent-bond-entity-view-layout"
			>
				<div
					data-testid="agent-bond-header-wrapper"
					className={cn(
						"shrink-0 grid bg-surface-base transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none",
						headerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
					)}
				>
					<div className="overflow-hidden min-h-0">
						<div
							className={cn(
								ENTITY_VIEW_COLUMN_CLASS,
								"flex flex-col space-y-[16px] pt-[18px]",
								!isCommercialSuretyEnabled && "pb-[18px]",
							)}
						>
							<BondCard
								bond={bond}
								actionButtons={actionButtons}
								followersControl={followersControl}
							/>
						</div>
					</div>
				</div>

				<div
					data-testid={headerCollapsed ? "agent-bond-collapsed-chrome" : undefined}
					className={cn(
						"relative bg-surface-base shrink-0 z-[25] border-b border-b-bc-subtle",
						isCommercialSuretyEnabled && "shadow-md shadow-gray-50",
						headerCollapsed &&
							!isCommercialSuretyEnabled &&
							"flex items-center justify-end px-8 py-2 shadow-md shadow-gray-50",
					)}
				>
					{isCommercialSuretyEnabled && (
						<div className={cn(ENTITY_VIEW_COLUMN_CLASS, "flex")}>
							<TabBar
								options={AGENT_BOND_TABS}
								activeTabId={activeTabId}
								onTabChange={handleTabChange}
								className="pt-4 gap-8"
								minimal
								trackingPrefix="bond-agent-tab"
							/>
						</div>
					)}
					{headerCollapsed ? (
						<div
							data-testid="agent-bond-collapsed-summary"
							className={
								isCommercialSuretyEnabled
									? "absolute right-8 top-1/2 -translate-y-1/2 hidden xs:block"
									: undefined
							}
						>
							<AgentBondStickyPanel
								bond={bond}
								followersControl={followersControl}
								alwaysShow={!isCommercialSuretyEnabled}
							/>
						</div>
					) : null}
				</div>

				<div
					data-testid="agent-bond-scroll-container"
					ref={setScrollRef}
					className="flex-1 min-h-0 overflow-auto overscroll-y-contain"
					onScroll={handlePageScroll}
				>
					<div
						className={cn(ENTITY_VIEW_COLUMN_CLASS, "flex flex-col space-y-[40px] mt-6 pb-[125px]")}
						data-testid="agent-bond-page-body"
					>
						{activeTabId === "periods-and-adjustments" ? (
							<AgentBondPeriodsTab accountId={bond.accountId} bondId={bond.id} />
						) : activeTabId === "bond-form" ? (
							<AgentBondFormTab bond={bond} />
						) : (
							<>
								{isCommercialSuretyEnabled ? (
									Option.isSome(currentTerm) && (
										<CurrentBondTermCard
											commission={deriveTermCommission(
												currentTerm.totalPremium,
												commissionPercentageForBond(bond),
											)}
											ordinal={bondTermOrdinal(terms, currentTerm)}
											term={currentTerm}
										/>
									)
								) : (
									<AgentBondFormTab bond={bond} />
								)}
								{bond.suretyType !== SuretyType.contract && <AgentBondDetailsCard bond={bond} />}
								<Card
									elevation="raised"
									className="rounded-xl"
									data-testid="agent-bond-messages-card"
								>
									<Card.Body className="p-6">
										<CommentsSection
											entityType="Bond"
											exportEntityType="bond"
											comments={comments}
											commentsLoading={commentsLoading}
											createComment={createComment}
											createCommentLoading={createCommentLoading}
											draftKey={messageDraftKey("bonds", bond.id)}
											commentDownloadHeader={`${bond.displayTitle} For ${bond.principalName}`}
											downloadTrackingId="bond-agent-comments-download"
										/>
									</Card.Body>
								</Card>
								<BondAttachmentsTable
									bondId={bond.id}
									allowDelete={false}
									allowUpload
									downloadAllName={`${bond.accountName} ${bond.displayTitle} Attachments`}
								/>
							</>
						)}
					</div>
				</div>
			</div>
		</AgentWorkflowLayout>
	);
};

export const BondView = () => {
	const { id } = useParams();
	const { bond, bondError, bondLoading } = useFetchBond(id!);
	const navigate = useNavigate();

	useEffect(() => {
		if (bondError) {
			navigate("/bonds");
		}
	}, [bondError, navigate]);

	if (bondLoading || bond === undefined) return <LoadingModal />;
	return <BondViewCore bond={bond} />;
};
